import { useCallback, useEffect, useRef, useState } from "react";

import { INITIAL_RESTORATION, type RestorationState } from "../components/RestorePanel";
import type { RestorePreview } from "../components/EditorCanvas";
import type { Rect } from "../editor/geometry";
import type { EditorImage, RestorationPatch } from "../editor/types";
import { composeSourceImage } from "../utils/files";
import { useRestorationProcessor } from "./useRestorationProcessor";

function cropImageData(source: ImageData, rect: Rect): ImageData {
  const x = Math.max(0, Math.floor(rect.x));
  const y = Math.max(0, Math.floor(rect.y));
  const width = Math.max(1, Math.min(source.width - x, Math.ceil(rect.width)));
  const height = Math.max(1, Math.min(source.height - y, Math.ceil(rect.height)));
  const pixels = new Uint8ClampedArray(width * height * 4);
  for (let row = 0; row < height; row += 1) {
    const start = ((y + row) * source.width + x) * 4;
    pixels.set(source.data.subarray(start, start + width * 4), row * width * 4);
  }
  return new ImageData(pixels, width, height);
}

function imageDataToBlob(imageData: ImageData): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext("2d");
  if (!context) return Promise.reject(new Error("プレビューCanvasを初期化できませんでした"));
  context.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("プレビュー画像を作成できませんでした"))),
      "image/png",
    );
  });
}

/**
 * AI restoration of a dragged rectangle: run inference, let the user compare
 * and tune the strength, then commit the result as a patch on the image.
 */
export function useRestoration(
  image: EditorImage | undefined,
  onApply: (patch: RestorationPatch) => void,
) {
  const processor = useRestorationProcessor();
  const [state, setState] = useState<RestorationState>(INITIAL_RESTORATION);
  const [preview, setPreview] = useState<RestorePreview | null>(null);
  const rectRef = useRef<Rect | null>(null);
  const requestRef = useRef(0);
  const urlsRef = useRef(new Set<string>());

  const revoke = (url: string) => {
    URL.revokeObjectURL(url);
    urlsRef.current.delete(url);
  };

  const cancel = useCallback(() => {
    requestRef.current += 1;
    processor.cancel();
    setPreview((current) => {
      if (current) revoke(current.dataUrl);
      return null;
    });
    rectRef.current = null;
    setState(INITIAL_RESTORATION);
  }, [processor]);

  const run = useCallback(
    async (rect: Rect) => {
      if (!image) return;
      rectRef.current = rect;
      const requestId = ++requestRef.current;
      const stale = () => requestId !== requestRef.current;
      // A new range supersedes in-flight inference instead of queueing behind it.
      processor.cancel();
      setState((current) => ({
        ...current,
        status: "processing",
        phase: "loading",
        completed: 0,
        total: 0,
        provider: undefined,
        error: undefined,
      }));
      try {
        const source = await composeSourceImage(image);
        if (stale()) return;
        const result = await processor(cropImageData(source, rect), state.strength, (progress) => {
          if (stale()) return;
          setState((current) => ({ ...current, status: "processing", ...progress }));
        });
        const url = URL.createObjectURL(await imageDataToBlob(result.imageData));
        urlsRef.current.add(url);
        if (stale()) {
          revoke(url);
          return;
        }
        setPreview((current) => {
          if (current) revoke(current.dataUrl);
          return {
            dataUrl: url,
            x: Math.max(0, Math.floor(rect.x)),
            y: Math.max(0, Math.floor(rect.y)),
            width: result.imageData.width,
            height: result.imageData.height,
            strength: state.strength,
            compare: state.compare,
          };
        });
        setState((current) => ({
          ...current,
          status: "ready",
          provider: result.provider,
          phase: undefined,
          completed: undefined,
          total: undefined,
        }));
      } catch (error) {
        if (stale()) return;
        setState((current) => ({
          ...current,
          status: "error",
          phase: undefined,
          completed: undefined,
          total: undefined,
          error: error instanceof Error ? error.message : "AI復元に失敗しました",
        }));
      }
    },
    [image, processor, state.compare, state.strength],
  );

  const apply = useCallback(() => {
    if (!preview) return;
    // The patch now owns the object URL; it is released with the image.
    urlsRef.current.delete(preview.dataUrl);
    onApply({
      id: crypto.randomUUID(),
      x: preview.x,
      y: preview.y,
      width: preview.width,
      height: preview.height,
      dataUrl: preview.dataUrl,
      strength: state.strength,
    });
    setPreview(null);
    rectRef.current = null;
    setState(INITIAL_RESTORATION);
  }, [onApply, preview, state.strength]);

  const change = useCallback((patch: Partial<RestorationState>) => {
    setState((current) => ({ ...current, ...patch }));
    setPreview((current) =>
      current
        ? {
            ...current,
            strength: patch.strength ?? current.strength,
            compare: patch.compare ?? current.compare,
          }
        : null,
    );
  }, []);

  const retry = useCallback(() => {
    if (rectRef.current) void run(rectRef.current);
  }, [run]);

  useEffect(() => {
    const urls = urlsRef.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  return { state, preview, run, apply, change, retry, cancel };
}
